import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { access, apiErrorSchema } from './schemas/common.js';
import { userChannelSchema, userRoleSchema } from './schemas/domain.js';
const c = initContract();
export const workspaceSchema = z.object({
    id: z.uuid(),
    slug: z.string(),
    name: z.string(),
    role: userRoleSchema,
});
/**
 * The signed-in person, as the panel needs them: enough to label and to gate
 * screens. `role` and `channels` are theirs in the active workspace; with none
 * yet (just signed up), `workspace` is null and the panel asks for one.
 */
export const meSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    role: userRoleSchema,
    channels: z.array(userChannelSchema),
    workspace: workspaceSchema.nullable(),
    workspaces: z.array(workspaceSchema),
});
const emailSchema = z.string().trim().min(3).max(254);
/**
 * The bot check's token (docs/turnstile.md): single use, 2048 characters at
 * most. Optional in the shape because a deployment without TURNSTILE_SECRET_KEY
 * asks for none; one with it refuses a request that carries none.
 */
const captchaTokenSchema = z.string().max(2048).optional();
const captchaFailedSchema = z.object({ error: z.literal('captcha_failed'), message: z.string() });
/**
 * Email + one-time code (docs/web-auth.md). `code` answers 202 for every
 * address, known or not; the session travels only as an httpOnly cookie.
 */
export const authContract = c.router({
    code: {
        method: 'POST',
        path: '/auth/code',
        metadata: access('public'),
        summary: 'Mail a sign-in code — answered identically whether or not the address has access',
        body: z.object({ email: emailSchema, captchaToken: captchaTokenSchema }),
        responses: {
            202: z.object({ resendAfterSeconds: z.number().int() }),
            400: captchaFailedSchema,
            429: apiErrorSchema,
            503: apiErrorSchema,
        },
    },
    verify: {
        method: 'POST',
        path: '/auth/verify',
        metadata: access('public'),
        summary: 'Trade the code for a session cookie',
        body: z.object({
            email: emailSchema,
            code: z.string().regex(/^\d{6}$/),
            captchaToken: captchaTokenSchema,
        }),
        responses: {
            200: z.object({ user: meSchema, created: z.boolean() }),
            400: z.object({ error: z.enum(['invalid_code', 'captcha_failed']), message: z.string() }),
            429: apiErrorSchema,
            503: apiErrorSchema,
        },
    },
    logout: {
        method: 'POST',
        path: '/auth/logout',
        metadata: access('account'),
        body: z.object({}),
        responses: { 204: c.noBody() },
    },
    logoutAll: {
        method: 'POST',
        path: '/auth/logout-all',
        metadata: access('account'),
        summary: 'Sign out of every device, this one included',
        body: z.object({}),
        responses: { 204: c.noBody() },
    },
    me: {
        method: 'GET',
        path: '/auth/me',
        metadata: access('account'),
        responses: { 200: z.object({ user: meSchema }) },
    },
});
//# sourceMappingURL=auth.contract.js.map